<template>
  <div
    class="block-grid"
  >
    <div
      v-for="(group, groupIndex) in renderGroups"
      :key="'blockgroup-' + groupIndex"
      class="block-grid__group"
      :class="group.rowClass"
    >
      <in-view
        v-for="(block, index) in group.items"
        :key="`${block.component}${index }${((block.recipe && block.recipe.id || ''))}`"
        v-edit:block="block.contentId"
        class="block-grid__block slide-into-view-transition"
        :class="getColSize(block, group)"
        :threshold="0"
        :md-threshold="30"
        :threshold-type="'px'"
      >
        <component
          :is="block.component | kebab"
          :tracking="{
            listItemIndex: String(groupIndex + index),
            ...tracking
          }"
          v-bind="block"
          @open-folder-modal="openFolderModal"
        />
        <slot />
      </in-view>
    </div>
  </div>
</template>

<script>
import { mapActions } from 'vuex'
import IcaAddSingleRecipeToFolderModal from "../molecules/AddSingleRecipeToFolderModal.vue"

export default {
  name: "BlockGrid",
  components: {
    IcaAddSingleRecipeToFolderModal
  },
  props: {
    blocks: {
      type: Array,
      default: () => []
    },
    // Forces non mobile rows to this amount (only prepared for three/third).
    forceRowSize: {
      type: String,
      default: "",
      validator (value) {
        return ["", "third"].includes(value)
      }
    },
    /* Forces a certain block size per viewport.
     * Will be overwritten by manual epi content (fixedSize).
     * Use syntax below where size is small, medium or large viewport and firstOnly applies only to first block in list.
     * Will only work on full och half
     * {
     *    isSmallViewport: {
     *      size: "full",
     *      firstOnly: true
     *    },
     *    isMediumViewport: {
     *      size: "half",
     *      firstOnly: false
     *    },
     *    isLargeViewport: {
     *      size: "full",
     *    }
     * }
     */
    customSize: {
      type: Object,
      default: undefined
    },
    tracking: {
      type: Object,
      default: function () {
        return {}
      }
    },
  },
  computed: {
    renderGroups () {
      let blockGroups = []
      let currentGroup = this.getNewGroup()

      let i = 1
      this.blocks.forEach( v => {
        let fixedSize = ""

        if (v.renderSettings) {
          fixedSize = v.renderSettings['data-epi-content-display-option']
        }

        // Force special size
        if (!v.fixedSize && this.customSize) {
          // Iterate the sizes set on customSize
          for (const [size, obj] of Object.entries(this.customSize)) {
            // If a certain size is set and the viewport matches
            if (obj && this.$root[size]) {
              // Only set fixedSize if onlyFirst matches or if not set
              fixedSize = !obj.firstOnly || (obj.firstOnly && i === 1) ? obj.size : ''
            }
          }
        }

        switch (fixedSize) {
            case "half":
              currentGroup.items.push(v)
              currentGroup.nrOfHalves++
              if (currentGroup.nrOfHalves === 2 && currentGroup.items.length === 2){
                blockGroups.push(currentGroup)
                currentGroup = this.getNewGroup()
              }
              break
            case "full":
              if (currentGroup.items.length !== 0) {
                blockGroups.push(currentGroup)
                currentGroup = this.getNewGroup()
              }

              currentGroup.items.push(v)
              blockGroups.push(currentGroup)
              currentGroup = this.getNewGroup()
              break
            default:
              currentGroup.items.push(v)
        }
        i++
        return v
      })
      blockGroups.push(currentGroup)

      blockGroups.forEach(group => {
        let nrOfBlocks = group.items.length + group.nrOfHalves
        if (group.items.length === 1) {
          group.rowClass = "row-1"
          group.items[0].blockSize = 'full'
        } else if (group.items.length === 2) {
          group.rowClass = "row-1_md-2"
          group.baseWidth = 3
          group.items[0].blockSize = 'half'
          group.items[1].blockSize = 'half'
        } else {
          const rowOfThreeHoles = nrOfBlocks%3 === 0 ? 0 : 3 - nrOfBlocks%3
          const rowOfFourHoles = nrOfBlocks%4 === 0 ? 0 : 4 - nrOfBlocks%4
          if (rowOfThreeHoles < rowOfFourHoles || this.forceRowSize == 'third') {
            group.rowClass = 'row-1_md-3'
            group.baseWidth = 4
          } else {
            group.rowClass = 'row-1_md-4'
            group.baseWidth = 3
          }

          group.items.forEach( item => {
            if (!item.fixedSize) {
              item.blockSize = group.baseWidth === 4 ? 'third' : 'quarter'
            } else {
              item.blockSize = item.fixedSize
            }
          })
        }
      })

      return blockGroups
    }
  },
  methods: {
    ...mapActions(['getPublicAccessToken']),
    openFolderModal (data) {
      this.$emit('open-folder-modal', data)
    },
    getNewGroup () {
      return {
        items: [],
        nrOfHalves: 0
      }
    },
    getColSize (block, group) {
      if (block.blockSize === "half") {
        const baseWidth = group.baseWidth || 4
        return `col-12_lg-${baseWidth * 2}`
      } else if (block.blockSize === "full") {
          `col-12`
      }
      return 'col'
    }
  },
}
</script>

<style lang="scss">
.block-grid {
  margin-bottom: -1rem;

  @include media-breakpoint-down(sm) {
    margin-right: -1rem;
    margin-left: -1rem;

    & &__group {
      margin: 0;
    }

    & &__block {
      padding-left: 0;
      padding-right: 0;
      padding-bottom: 1rem;
    }
  }
}
</style>
